/**
 * The money moments of an investment, each previewed before it is saved:
 *
 *   Declare dividend         an entity's declaration, allocated to recorded holders
 *   Post profit share        the period's calculation, line by line
 *   Record payment           money paid to an equity holder or profit sharer
 *   Correct dates and terms  posted money recalculated, the difference recorded
 *   Change vehicle terms     a new share for every linked vehicle, from a date
 *
 * and two pieces of set-up: the entities equity is held in, and who may
 * manage investments (the owner's decision).
 */

import { useState, type ReactNode } from 'react';
import {
  CURRENCIES,
  INVESTMENT_PERMISSIONS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  VEHICLE_PAYOUT_BASES,
  VEHICLE_PAYOUT_BASIS_LABELS,
  type DividendInput,
  type InvestmentDetail,
  type InvestmentPermission,
  type ProfitBreakdown,
  type VehiclePayoutBasis,
} from '@cenox/shared';
import { Button, ErrorState, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { AccountSelect } from '../money/AccountSelect';
import {
  useDividend,
  useEntities,
  useInvestmentAccess,
  useInvestmentCorrection,
  useNextProfitPeriod,
  useProfitShare,
  useRecordInvestmentPayment,
  useSaveEntity,
  useSetInvestmentAccess,
  useVehicleTerms,
} from './investmentData';

const METHODS = PAYMENT_METHODS.filter((m) => m !== 'credit');
const fieldsOf = (e: unknown) => (e instanceof ApiError ? (e.fields as Record<string, string[] | undefined> | undefined) : undefined);
const key = (p: string) => `${p}-${crypto.randomUUID()}`;

function Flags({ flags }: { flags: string[] }): ReactNode {
  if (!flags.length) return null;
  return (
    <ul aria-label="Needs attention" className="space-y-1 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
      {flags.map((f) => (
        <li key={f}>{f}</li>
      ))}
    </ul>
  );
}

function Problem({ error }: { error: unknown }): ReactNode {
  if (!error) return null;
  return <ErrorState message={error instanceof Error ? error.message : 'Something went wrong.'} />;
}

// ── Dividends ──────────────────────────────────────────────────────────────

export function DividendDialog({ onClose, onDone }: { onClose: () => void; onDone: (m: string) => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const entities = useEntities();
  const { preview, post } = useDividend();
  const [input, setInput] = useState<DividendInput>({
    entityId: '',
    ownershipBasis: 'Ordinary shares',
    recordDate: today,
    declaredOn: today,
    currencyCode: business?.currencyCode ?? 'NGN',
    totalAmount: '',
  });
  const [idem] = useState(() => key('dividend'));
  const fields = fieldsOf(preview.error ?? post.error);
  const result = preview.data?.data;
  const set = (patch: Partial<DividendInput>) => {
    setInput({ ...input, ...patch });
    preview.reset();
  };

  return (
    <Modal open onClose={onClose} title="Declare a dividend" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Equity earns nothing by itself. A dividend is what an entity declares; each holder recorded on the record date
          is allocated their percentage of it. Preview it before posting.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Entity"
            value={input.entityId}
            placeholder="Choose…"
            options={(entities.data ?? []).map((e) => ({ value: e.id, label: e.name }))}
            onChange={(e) => set({ entityId: e.target.value })}
            error={fields?.entityId?.[0]}
          />
          <Input label="Ownership basis" value={input.ownershipBasis} onChange={(e) => set({ ownershipBasis: e.target.value })} error={fields?.ownershipBasis?.[0]} />
          <Input label="Total declared" inputMode="decimal" value={input.totalAmount} onChange={(e) => set({ totalAmount: e.target.value })} error={fields?.totalAmount?.[0]} />
          <Select
            label="Currency"
            value={input.currencyCode}
            options={CURRENCIES.map((c) => ({ value: c.code, label: c.code }))}
            onChange={(e) => set({ currencyCode: e.target.value })}
          />
          <Input label="Record date" type="date" max={today} value={input.recordDate} onChange={(e) => set({ recordDate: e.target.value })} hint="Holders on this day receive it" error={fields?.recordDate?.[0]} />
          <Input label="Declared on" type="date" max={today} value={input.declaredOn} onChange={(e) => set({ declaredOn: e.target.value })} error={fields?.declaredOn?.[0]} />
        </div>
        {result ? (
          <section data-testid="dividend-preview" className="space-y-2">
            <table className="w-full text-sm" aria-label="Allocations">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-1.5">Holder</th>
                  <th className="py-1.5 text-right">Holds</th>
                  <th className="py-1.5 text-right">Allocated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.allocations.map((a) => (
                  <tr key={a.investmentId}>
                    <td className="py-1.5">{a.investorName}</td>
                    <td className="py-1.5 text-right tabular-nums">{a.percent}%</td>
                    <td className="py-1.5 text-right tabular-nums">{money(a.amount, result.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-sm text-slate-600">
              Allocated {money(result.allocatedTotal, result.currencyCode)} of {money(result.totalAmount, result.currencyCode)}.{' '}
              {Number(result.unallocated) > 0 ? `${money(result.unallocated, result.currencyCode)} belongs to holders not recorded here and is not allocated.` : ''}
            </p>
            <Flags flags={result.flags} />
          </section>
        ) : null}
        {(preview.error && !fields) || (post.error && !fields) ? <Problem error={preview.error ?? post.error} /> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" loading={preview.isPending} disabled={!input.entityId || !input.totalAmount} onClick={() => preview.mutate(input)}>
            Preview allocation
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            loading={post.isPending}
            disabled={!result || result.allocations.length === 0 || post.isPending}
            onClick={() => post.mutate({ ...input, idempotencyKey: idem }, { onSuccess: (r) => onDone(`Dividend posted: ${money(r.data.allocatedTotal, r.data.currencyCode)} allocated.`) })}
          >
            Post dividend
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Entities ───────────────────────────────────────────────────────────────

export function EntitiesDialog({ mayManage, onClose }: { mayManage: boolean; onClose: () => void }): ReactNode {
  const entities = useEntities();
  const save = useSaveEntity();
  const [name, setName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const fields = fieldsOf(save.error);
  return (
    <Modal open onClose={onClose} title="Entities" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          The businesses and legal entities equity is held in. Ownership is totalled per entity and basis — never across
          entities.
        </p>
        {entities.isPending ? (
          <Spinner />
        ) : (entities.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No entity recorded yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" aria-label="Entities">
            {entities.data!.map((e) => (
              <li key={e.id} className="px-3 py-2 text-sm">
                <span className="font-medium text-slate-900">{e.name}</span>
                {e.registrationNumber ? <span className="text-slate-400"> · {e.registrationNumber}</span> : null}
                <span className="block text-xs text-slate-500">
                  {e.ownership.length
                    ? e.ownership.map((o) => `${o.percent}% recorded on ${o.basis}`).join(' · ')
                    : 'No equity recorded in force today'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {mayManage ? (
          <form
            className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(
                { id: null, input: { name, ...(registrationNumber ? { registrationNumber } : {}) } },
                { onSuccess: () => { setName(''); setRegistrationNumber(''); } },
              );
            }}
          >
            <Input label="Entity name" value={name} onChange={(e) => setName(e.target.value)} error={fields?.name?.[0]} />
            <Input label="Registration number" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
            <Button type="submit" disabled={!name.trim()} loading={save.isPending}>
              Add entity
            </Button>
          </form>
        ) : null}
      </div>
    </Modal>
  );
}

// ── Who may manage investments ─────────────────────────────────────────────

const PERMISSION_TEXT: Record<InvestmentPermission, string> = {
  'investments:read': 'See investments',
  'investments:manage': 'Change investments and investors',
  'investments:post': 'Post dividends, profit shares and payments',
};

export function AccessDialog({ onClose }: { onClose: () => void }): ReactNode {
  const access = useInvestmentAccess(true);
  const set = useSetInvestmentAccess();
  return (
    <Modal open onClose={onClose} title="Who may manage investments" size="lg">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          The owner holds everything. Administrators can see investments; changing them and posting money is the owner’s
          unless given here. Location logins and workers never can.
        </p>
        {access.isPending ? (
          <Spinner />
        ) : access.isError ? (
          <ErrorState message={access.error.message} />
        ) : (
          <ul className="divide-y divide-slate-100" aria-label="Members">
            {access.data!.map((m) => (
              <li key={m.memberId} className="py-2">
                <p className="text-sm font-medium text-slate-900">
                  {m.name} <span className="text-xs font-normal text-slate-500">· {m.role}</span>
                </p>
                <div className="mt-1 flex flex-wrap gap-3">
                  {INVESTMENT_PERMISSIONS.map((p) => (
                    <label key={p} className="flex items-center gap-1.5 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        className="size-4 accent-violet-600"
                        checked={m.permissions.includes(p)}
                        disabled={!m.editable || set.isPending}
                        onChange={(e) =>
                          set.mutate({
                            memberId: m.memberId,
                            permissions: e.target.checked ? [...m.permissions, p] : m.permissions.filter((x) => x !== p),
                          })
                        }
                      />
                      {PERMISSION_TEXT[p]}
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Problem error={set.error} />
      </div>
    </Modal>
  );
}

// ── Profit shares ──────────────────────────────────────────────────────────

export function BreakdownTable({ b }: { b: ProfitBreakdown }): ReactNode {
  const c = b.currencyCode;
  return (
    <section data-testid="profit-breakdown" className="space-y-3 text-sm">
      <p className="text-slate-600">
        {b.periodStart} – {b.periodEnd} ·{' '}
        {b.includesBusinessWide ? 'every location, and business-wide income' : b.locations.length ? b.locations.map((l) => l.name).join(', ') : 'no location in this scope yet'}
      </p>
      <table className="w-full" aria-label="Profit calculation">
        <tbody className="divide-y divide-slate-100">
          {b.income.map((i) => (
            <tr key={i.stream}>
              <td className="py-1">
                {i.label}
                {i.note ? <span className="block text-xs text-slate-500">{i.note}</span> : null}
              </td>
              <td className="py-1 text-right tabular-nums">{money(i.amount, c)}</td>
            </tr>
          ))}
          <tr className="font-medium">
            <td className="py-1">Eligible income</td>
            <td className="py-1 text-right tabular-nums">{money(b.incomeTotal, c)}</td>
          </tr>
          {b.expenses.map((e) => (
            <tr key={e.categoryId ?? e.name}>
              <td className="py-1 pl-3 text-slate-600">less {e.name}</td>
              <td className="py-1 text-right tabular-nums text-slate-600">−{money(e.amount, c)}</td>
            </tr>
          ))}
          <tr className="font-medium">
            <td className="py-1">Profit</td>
            <td className="py-1 text-right tabular-nums">{money(b.profit, c)}</td>
          </tr>
          {b.lossTreatment === 'carry_forward' ? (
            <tr>
              <td className="py-1 text-slate-600">less loss brought forward</td>
              <td className="py-1 text-right tabular-nums text-slate-600">−{money(b.lossBroughtForward, c)}</td>
            </tr>
          ) : null}
          <tr className="font-medium">
            <td className="py-1">Profit after losses</td>
            <td className="py-1 text-right tabular-nums">{money(b.profitAfterLosses, c)}</td>
          </tr>
          <tr className="font-semibold text-violet-800">
            <td className="py-1.5">Share at {b.percent}%</td>
            <td className="py-1.5 text-right tabular-nums">{money(b.share, c)}</td>
          </tr>
          {b.lossTreatment === 'carry_forward' ? (
            <tr>
              <td className="py-1 text-slate-600">Loss carried to the next period</td>
              <td className="py-1 text-right tabular-nums text-slate-600">{money(b.lossCarriedOut, c)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {b.otherCurrencies.length ? (
        <p className="text-xs text-slate-500">
          Not counted (other currencies, never converted):{' '}
          {b.otherCurrencies.map((o) => `${o.currencyCode} income ${o.income}, expenses ${o.expenses}`).join('; ')}
        </p>
      ) : null}
    </section>
  );
}

export function ProfitShareDialog({ investment, onClose, onDone }: { investment: InvestmentDetail; onClose: () => void; onDone: (m: string) => void }): ReactNode {
  const next = useNextProfitPeriod(investment.id);
  const { preview, post } = useProfitShare();
  const [periodStart, setPeriodStart] = useState<string | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [idem] = useState(() => key('profit'));
  const start = periodStart ?? next.data?.periodStart ?? '';
  const end = periodEnd ?? next.data?.periodEnd ?? '';
  const fields = fieldsOf(preview.error ?? post.error);
  const result = preview.data?.data;
  return (
    <Modal open onClose={onClose} title={`Profit share — ${investment.investorName}`} size="lg">
      <div className="space-y-4">
        {next.data?.blocked ? <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{next.data.blocked}</p> : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Period from" type="date" value={start} onChange={(e) => { setPeriodStart(e.target.value); preview.reset(); }} error={fields?.periodStart?.[0]} />
          <Input label="to" type="date" value={end} onChange={(e) => { setPeriodEnd(e.target.value); preview.reset(); }} error={fields?.periodEnd?.[0]} />
        </div>
        {result ? (
          <>
            <BreakdownTable b={result.breakdown} />
            <Flags flags={result.flags} />
          </>
        ) : null}
        {(preview.error || post.error) && !fields ? <Problem error={preview.error ?? post.error} /> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="secondary" disabled={!start || !end} loading={preview.isPending} onClick={() => preview.mutate({ id: investment.id, input: { periodStart: start, periodEnd: end } })}>
            Calculate
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!result || post.isPending}
            loading={post.isPending}
            onClick={() =>
              post.mutate(
                { id: investment.id, input: { periodStart: start, periodEnd: end, idempotencyKey: idem } },
                { onSuccess: (r) => onDone(`Profit share posted: ${money(r.data.breakdown.share, r.data.breakdown.currencyCode)}.`) },
              )
            }
          >
            Post profit share
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Payments to equity holders and profit sharers ──────────────────────────

export function InvestmentPaymentDialog({ investment, onClose, onDone }: { investment: InvestmentDetail; onClose: () => void; onDone: (m: string) => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const record = useRecordInvestmentPayment();
  const currency = investment.money.currencyCode;
  const [amount, setAmount] = useState(investment.money.outstanding ?? '');
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<(typeof METHODS)[number]>('bank_transfer');
  const [accountId, setAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [idem] = useState(() => key('investment-payment'));
  const fields = fieldsOf(record.error);
  const duplicate = Boolean(fields?.confirmDuplicate);
  const submit = (confirm: boolean) =>
    record.mutate(
      {
        id: investment.id,
        input: {
          amount, paidOn, method,
          ...(accountId ? { accountId } : {}),
          ...(reference ? { reference } : {}),
          idempotencyKey: confirm ? `${idem}-confirmed` : idem,
          ...(confirm ? { confirmDuplicate: true } : {}),
        },
      },
      { onSuccess: () => onDone(`Payment of ${money(amount, currency)} recorded.`) },
    );
  return (
    <Modal open onClose={onClose} title={`Pay ${investment.investorName}`}>
      <form className="space-y-3" aria-label="Record investment payment" onSubmit={(e) => { e.preventDefault(); submit(false); }}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label={`Amount (${currency})`} inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} hint={`Outstanding: ${money(investment.money.outstanding ?? '0', currency)}`} error={fields?.amount?.[0]} />
          <Input label="Paid on" type="date" max={today} required value={paidOn} onChange={(e) => setPaidOn(e.target.value)} error={fields?.paidOn?.[0]} />
          <Select label="Method" value={method} options={METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] ?? m }))} onChange={(e) => { setMethod(e.target.value as (typeof METHODS)[number]); setAccountId(''); }} />
          <Input label="Transaction reference" value={reference} onChange={(e) => setReference(e.target.value)} />
        </div>
        <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fields?.accountId?.[0]} />
        {record.error && (!fields || duplicate) ? <Problem error={record.error} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          {duplicate ? (
            <Button type="button" onClick={() => submit(true)}>It is a separate payment — record it</Button>
          ) : (
            <Button type="submit" className="bg-violet-600 hover:bg-violet-700" loading={record.isPending} disabled={!amount || record.isPending}>
              Record payment
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ── Correcting dates and percentages after money was posted ───────────────

export function CorrectionDialog({ investment, onClose, onDone }: { investment: InvestmentDetail; onClose: () => void; onDone: (m: string) => void }): ReactNode {
  const { preview, post } = useInvestmentCorrection();
  const equity = investment.type === 'equity';
  const [startsOn, setStartsOn] = useState(investment.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(investment.endsOn ?? '');
  const [percent, setPercent] = useState((equity ? investment.ownershipPercent : investment.profitPercent) ?? '');
  const [reason, setReason] = useState('');
  const [idem] = useState(() => key('correction'));
  const fields = fieldsOf(preview.error ?? post.error);
  const result = preview.data?.data;
  const input = {
    reason,
    ...(startsOn && startsOn !== investment.startsOn ? { startsOn } : {}),
    ...(endsOn !== (investment.endsOn ?? '') ? { endsOn: endsOn || null } : {}),
    ...(percent && percent !== (equity ? investment.ownershipPercent : investment.profitPercent)
      ? equity ? { ownershipPercent: percent } : { profitPercent: percent }
      : {}),
  };
  const currency = result?.currencyCode ?? investment.currencyCode ?? '';
  return (
    <Modal open onClose={onClose} title="Correct dates and terms" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Posted {equity ? 'dividends' : 'profit shares'} are never rewritten. The difference each one would allocate under
          the corrected terms is recorded as an adjustment, dated today. Payments stay as they are.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Start" type="date" value={startsOn} onChange={(e) => { setStartsOn(e.target.value); preview.reset(); }} error={fields?.startsOn?.[0]} />
          <Input label="End" type="date" value={endsOn} onChange={(e) => { setEndsOn(e.target.value); preview.reset(); }} error={fields?.endsOn?.[0]} />
          <Input label={equity ? 'Ownership %' : 'Profit %'} inputMode="decimal" value={percent} onChange={(e) => { setPercent(e.target.value); preview.reset(); }} error={fields?.ownershipPercent?.[0] ?? fields?.profitPercent?.[0]} />
        </div>
        <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} error={fields?.reason?.[0]} />
        {result ? (
          <section data-testid="correction-preview" className="space-y-2">
            {result.distributions.length === 0 ? (
              <p className="text-sm text-slate-600">No posted money changes.</p>
            ) : (
              <table className="w-full text-sm" aria-label="Affected distributions">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                    <th className="py-1.5">Period</th>
                    <th className="py-1.5 text-right">Allocated</th>
                    <th className="py-1.5 text-right">Should be</th>
                    <th className="py-1.5 text-right">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.distributions.map((d) => (
                    <tr key={d.distributionId}>
                      <td className="py-1.5">{d.label}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(d.before, currency)}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(d.after, currency)}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums">{money(d.difference, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-sm font-medium text-slate-800">Total difference: {money(result.totalDifference, currency)}</p>
            <Flags flags={result.flags} />
          </section>
        ) : null}
        {(preview.error || post.error) && !fields ? <Problem error={preview.error ?? post.error} /> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" disabled={!reason.trim()} loading={preview.isPending} onClick={() => preview.mutate({ id: investment.id, input })}>
            Preview changes
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!result?.changed || post.isPending}
            loading={post.isPending}
            onClick={() => post.mutate({ id: investment.id, input: { ...input, idempotencyKey: idem } }, { onSuccess: (r) => onDone(r.data.changed ? `Corrected: ${r.data.distributions.length} adjustment(s).` : 'Nothing needed changing.') })}
          >
            Save and recalculate
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Vehicle partnership: new terms from a date ─────────────────────────────

export function VehicleTermsDialog({ investment, onClose, onDone }: { investment: InvestmentDetail; onClose: () => void; onDone: (m: string) => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const currency = investment.money.currencyCode;
  const { preview, post } = useVehicleTerms();
  const [basis, setBasis] = useState<VehiclePayoutBasis>(investment.vehiclePayoutBasis ?? 'fixed_per_period');
  const [fixedAmount, setFixedAmount] = useState(investment.vehicleFixedAmount ?? '');
  const [percent, setPercent] = useState(investment.vehiclePercent ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [reason, setReason] = useState('');
  const [idem] = useState(() => key('vehicle-terms'));
  const fields = fieldsOf(preview.error ?? post.error);
  const result = preview.data?.data;
  const input = {
    basis,
    ...(basis === 'fixed_per_period' ? { fixedAmount } : { percent }),
    effectiveFrom,
    reason,
  };
  const reset = () => preview.reset();
  return (
    <Modal open onClose={onClose} title="Change vehicle terms" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          The new share applies to every vehicle linked under this partnership from the date. Weeks already paid are
          re-divided by correcting entries on the payments’ own dates; no payment, receipt or payout is created.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label="Calculation basis" value={basis} options={VEHICLE_PAYOUT_BASES.map((b) => ({ value: b, label: VEHICLE_PAYOUT_BASIS_LABELS[b] }))} onChange={(e) => { setBasis(e.target.value as VehiclePayoutBasis); reset(); }} />
          {basis === 'fixed_per_period' ? (
            <Input label={`Amount a week (${currency})`} inputMode="decimal" value={fixedAmount} onChange={(e) => { setFixedAmount(e.target.value); reset(); }} error={fields?.fixedAmount?.[0]} />
          ) : (
            <Input label="Percentage of remittance" inputMode="decimal" value={percent} onChange={(e) => { setPercent(e.target.value); reset(); }} error={fields?.percent?.[0]} />
          )}
          <Input label="From" type="date" max={today} value={effectiveFrom} onChange={(e) => { setEffectiveFrom(e.target.value); reset(); }} error={fields?.effectiveFrom?.[0]} />
          <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} error={fields?.reason?.[0]} />
        </div>
        {result ? (
          <section data-testid="vehicle-terms-preview" className="space-y-2 text-sm">
            <table className="w-full" aria-label="Investor figures">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-1.5" />
                  <th className="py-1.5 text-right">Before</th>
                  <th className="py-1.5 text-right">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(
                  [
                    ['Entitlement accrued', 'entitled'],
                    ['Collected from riders', 'collected'],
                    ['Still unpaid by riders', 'unpaidByRiders'],
                    ['Paid out', 'paidOut'],
                    ['Owed to the investor', 'owed'],
                  ] as const
                ).map(([label, k]) => (
                  <tr key={k}>
                    <td className="py-1.5">{label}</td>
                    <td className="py-1.5 text-right tabular-nums">{money(result.figuresBefore[k], currency)}</td>
                    <td className="py-1.5 text-right tabular-nums">{money(result.figuresAfter[k], currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>
              Vehicles: {result.vehicles.map((v) => v.bike.registration).join(', ') || 'none linked from that date'} · company revenue{' '}
              {money(result.companyRevenueAdjustment, currency)}
            </p>
            <Flags flags={result.flags} />
          </section>
        ) : null}
        {(preview.error || post.error) && !fields ? <Problem error={preview.error ?? post.error} /> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" disabled={!reason.trim()} loading={preview.isPending} onClick={() => preview.mutate({ id: investment.id, input })}>
            Preview changes
          </Button>
          <Button
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!result?.changed || post.isPending}
            loading={post.isPending}
            onClick={() => post.mutate({ id: investment.id, input: { ...input, idempotencyKey: idem } }, { onSuccess: () => onDone('Vehicle terms changed and recalculated.') })}
          >
            Save and recalculate
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Void a distribution or payment, with the reason the audit keeps. */
export function VoidDialog({ title, onConfirm, onClose, pending, error }: { title: string; onConfirm: (reason: string) => void; onClose: () => void; pending: boolean; error: unknown }): ReactNode {
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title={title}>
      <div className="space-y-3">
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        <Problem error={error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!reason.trim()} loading={pending} onClick={() => onConfirm(reason)}>
            Void
          </Button>
        </div>
      </div>
    </Modal>
  );
}
